import React, { useState, useEffect } from 'react';
import { Box, Button, Paper, Typography, TextField, Alert } from '@mui/material';
import { CheckCircle as CompleteIcon, Replay as RetryIcon } from '@mui/icons-material';

interface FillInBlankPlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

interface BlankSegment {
    type: 'text' | 'blank';
    content: string;
    correctAnswers?: string[];
    index?: number;
}

const FillInBlankPlayer: React.FC<FillInBlankPlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const [segments, setSegments] = useState<BlankSegment[]>([]);
    const [userAnswers, setUserAnswers] = useState<{ [key: number]: string }>({});
    const [submitted, setSubmitted] = useState(false);
    const [results, setResults] = useState<{ [key: number]: boolean }>({});
    const [score, setScore] = useState(0);
    const [totalBlanks, setTotalBlanks] = useState(0);

    const content = lesson.content || {};
    const text = content.text || '';
    const hint = content.hint || '';
    const explanation = content.explanation || '';

    useEffect(() => {
        parseText();
    }, [text]);

    const parseText = () => {
        if (!text) return;

        const regex = /\{\{([^}]+)\}\}/g;
        const parsed: BlankSegment[] = [];
        let lastIndex = 0;
        let blankIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
            // Add text before the blank
            if (match.index > lastIndex) {
                parsed.push({
                    type: 'text',
                    content: text.substring(lastIndex, match.index)
                });
            }

            // Add blank
            const answersString = match[1];
            const correctAnswers = answersString.split('|').map(a => a.trim().toLowerCase());

            parsed.push({
                type: 'blank',
                content: answersString,
                correctAnswers,
                index: blankIndex
            });

            blankIndex++;
            lastIndex = regex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            parsed.push({
                type: 'text',
                content: text.substring(lastIndex)
            });
        }

        setSegments(parsed);
        setTotalBlanks(blankIndex);
    };

    const handleAnswerChange = (index: number, value: string) => {
        setUserAnswers({
            ...userAnswers,
            [index]: value
        });
    };

    const handleSubmit = () => {
        const newResults: { [key: number]: boolean } = {};
        let correctCount = 0;

        segments.forEach(segment => {
            if (segment.type === 'blank' && segment.index !== undefined) {
                const userAnswer = (userAnswers[segment.index] || '').trim().toLowerCase();
                const isCorrect = segment.correctAnswers?.some(
                    answer => answer === userAnswer
                ) || false;

                newResults[segment.index] = isCorrect;
                if (isCorrect) correctCount++;
            }
        });

        setResults(newResults);
        setScore(Math.round((correctCount / totalBlanks) * 100));
        setSubmitted(true);
    };

    const handleRetry = () => {
        setUserAnswers({});
        setResults({});
        setSubmitted(false);
        setScore(0);
    };

    const handleMarkComplete = async () => {
        // Issue #332: Score ans Backend melden, bevor die Lektion abgeschlossen
        // wird — awaited vor onComplete, damit lesson_progress.score persistiert
        // ist, bevor /complete den Zertifikats-Score aggregiert.
        if (onProgressUpdate) {
            await onProgressUpdate(lesson.id, {
                score: String(score),
                status: 'completed',
                progressPercentage: 100,
            });
        }
        onComplete();
    };

    const allFilled = segments.filter(s => s.type === 'blank').every(
        s => s.index !== undefined && userAnswers[s.index]?.trim()
    );

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                {hint && !submitted && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Hinweis:</strong> {hint}
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ mt: 3, lineHeight: 2 }}>
                    {segments.map((segment, idx) => {
                        if (segment.type === 'text') {
                            return (
                                <span key={idx} style={{ whiteSpace: 'pre-wrap' }}>
                                    {segment.content}
                                </span>
                            );
                        } else {
                            const blankIndex = segment.index!;
                            const isCorrect = results[blankIndex];
                            const showResult = submitted;

                            return (
                                <TextField
                                    key={idx}
                                    size="small"
                                    value={userAnswers[blankIndex] || ''}
                                    onChange={(e) => handleAnswerChange(blankIndex, e.target.value)}
                                    disabled={submitted}
                                    sx={{
                                        mx: 0.5,
                                        width: '150px',
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: showResult
                                                ? isCorrect
                                                    ? 'success.light'
                                                    : 'error.light'
                                                : 'background.paper'
                                        }
                                    }}
                                    error={showResult && !isCorrect}
                                />
                            );
                        }
                    })}
                </Box>

                {submitted && (
                    <Box sx={{ mt: 3 }}>
                        <Alert severity={score >= 70 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                            <Typography variant="body1">
                                <strong>Ergebnis: {score}%</strong> ({Object.values(results).filter(r => r).length} von {totalBlanks} richtig)
                            </Typography>
                        </Alert>

                        {explanation && (
                            <Alert severity="info">
                                <Typography variant="body2">
                                    <strong>Erklärung:</strong> {explanation}
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {!submitted ? (
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSubmit}
                        disabled={!allFilled}
                    >
                        Überprüfen
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<RetryIcon />}
                            onClick={handleRetry}
                        >
                            Erneut versuchen
                        </Button>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<CompleteIcon />}
                            onClick={handleMarkComplete}
                        >
                            Als abgeschlossen markieren
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default FillInBlankPlayer;
